import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged, EmailAuthProvider, reauthenticateWithCredential, updatePassword, updateProfile, sendPasswordResetEmail } from 'firebase/auth';
import { useCart } from '../../context/CartContext'; 
import { useWishlist } from '../../context/WishlistContext'; 
import toast from 'react-hot-toast'; 
import { 
  User, Package, MapPin, Heart, Bell, Shield, 
  LogOut, ChevronRight, Clock, CheckCircle2, Truck, Sparkles, Edit3, Trash2, ShoppingCart, AlertTriangle 
} from 'lucide-react';

export default function CustomerProfile() {
  const navigate = useNavigate();
  const { cart, addToCart } = useCart();
  const { wishlist, removeFromWishlist } = useWishlist();

  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');

  // Security & Password Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [securityLoading, setSecurityLoading] = useState(false);

  // Edit Profile Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  // Logout Confirmation Modal State
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        let userData = {
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'Valued Customer',
          email: currentUser.email,
          phone: currentUser.phoneNumber || '+91 98765 43210',
          avatar: currentUser.photoURL || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300'
        };

        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            userData = { ...userData, ...userDocSnap.data() };
          }
        } catch (err) {
          console.log("Firestore user doc fetch warning (using auth fallback):", err);
        }

        setUser(userData);
        setEditName(userData.name || '');
        setEditPhone(userData.phone || '');

        try {
          const ordersQuery = query(collection(db, 'orders'), where('userId', '==', currentUser.uid));
          const orderSnap = await getDocs(ordersQuery);
          const orderList = orderSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setOrders(orderList);
        } catch (err) {
          console.log("Orders fetch warning:", err);
          setOrders([]);
        }

      } catch (error) {
        console.error("Critical error in profile loading:", error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Professional Logout Handler with Popup Confirmation Execution
  const handleConfirmLogout = async () => {
    try {
      setLogoutLoading(true);
      await auth.signOut();
      toast.success('Signed out successfully!');
      setIsLogoutModalOpen(false);
      navigate('/login');
    } catch (error) {
      console.error("Logout Error:", error);
      toast.error('Failed to sign out.');
    } finally {
      setLogoutLoading(false);
    }
  };

  // Working Edit Profile Submission Handler
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      await updateProfile(currentUser, { displayName: editName });

      const userDocRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userDocRef, {
        name: editName,
        phone: editPhone
      });

      setUser(prev => ({ ...prev, name: editName, phone: editPhone }));
      toast.success('Profile updated successfully!');
      setIsEditModalOpen(false);
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error('Failed to update profile.');
    } finally {
      setEditLoading(false);
    }
  };

  const handleMoveToCart = (product) => {
    addToCart({ ...product, quantity: 1 });
    removeFromWishlist(product.id);
    toast.success(`${product.name || product.productName || 'Product'} moved to cart!`, {
      icon: '🛒',
    });
  };

  // Password Change Handler with Re-authentication
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password should be at least 6 characters long.');
      return;
    }

    setSecurityLoading(true);
    try {
      const currentUser = auth.currentUser;
      const credential = EmailAuthProvider.credential(currentUser.email, oldPassword);
      await reauthenticateWithCredential(currentUser, credential);
      
      await updatePassword(currentUser, newPassword);
      
      setSuccessMessage('Password successfully updated!');
      toast.success('Password successfully updated!');
      setSecurityLoading(false);
      setTimeout(() => {
        setIsModalOpen(false);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setSuccessMessage('');
      }, 2000);
    } catch (err) {
      setSecurityLoading(false);
      if (err.code === 'auth/wrong-password') {
        setError('Incorrect old password entered.');
      } else {
        setError(err.message);
      }
    }
  };

  const handleForgotPasswordMail = async () => {
    try {
      setSecurityLoading(true);
      const currentUser = auth.currentUser;
      await sendPasswordResetEmail(auth, currentUser.email);
      setSecurityLoading(false);
      setSuccessMessage(`Password reset link sent successfully to ${currentUser.email}`);
      toast.success('Password reset email sent!');
    } catch (err) {
      setSecurityLoading(false);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-gray-50 text-gray-400 font-medium">
        <div className="flex flex-col items-center space-y-3">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-xs tracking-wider uppercase font-bold text-gray-500">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* TOP BANNER / PROFILE HEADER CARD */}
        <div className="relative bg-gradient-to-r from-gray-900 via-indigo-950 to-purple-950 rounded-3xl p-6 sm:p-8 text-white shadow-xl overflow-hidden border border-gray-800">
          <div className="absolute -right-10 -bottom-10 w-60 h-60 bg-indigo-500/20 rounded-full blur-3xl pointer-events-none"></div>
          
          <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start justify-between gap-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 text-center sm:text-left">
              <div className="relative">
                <img 
                  src={user?.avatar || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300"} 
                  alt="Profile" 
                  className="w-24 h-24 rounded-full object-cover ring-4 ring-white/20 shadow-2xl"
                />
                <span className="absolute bottom-0 right-0 bg-emerald-500 w-5 h-5 rounded-full border-2 border-gray-950 flex items-center justify-center text-[10px]" title="Active Status"></span>
              </div>
              <div>
                <div className="flex items-center justify-center sm:justify-start space-x-2">
                  <h1 className="text-xl sm:text-2xl font-black tracking-tight">{user?.name || 'Customer Name'}</h1>
                  <span className="bg-amber-400/20 text-amber-300 border border-amber-400/30 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center space-x-1">
                    <Sparkles className="w-3 h-3" />
                    <span>Prime Member</span>
                  </span>
                </div>
                <p className="text-gray-300 text-xs sm:text-sm mt-1">{user?.email}</p>
                <p className="text-gray-400 text-xs mt-0.5">{user?.phone || '+91 98765 43210'}</p>
              </div>
            </div>

            <div className="flex flex-row sm:flex-col gap-3 w-full sm:w-auto">
              {/* Edit Profile Button */}
              <button 
                onClick={() => setIsEditModalOpen(true)}
                className="flex-1 sm:flex-none bg-white/10 hover:bg-white/20 text-white border border-white/20 px-4 py-2.5 rounded-xl font-bold text-xs transition flex items-center justify-center space-x-2 backdrop-blur-md"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>Edit Profile</span>
              </button>
              {/* Sign Out Button triggers Popup */}
              <button 
                onClick={() => setIsLogoutModalOpen(true)}
                className="flex-1 sm:flex-none bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 px-4 py-2.5 rounded-xl font-bold text-xs transition flex items-center justify-center space-x-2 backdrop-blur-md"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>

        {/* STATS QUICK CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center space-x-4">
            <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center font-bold">
              <Package className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Total Orders</p>
              <p className="text-xl font-black text-gray-900 mt-0.5">{orders.length}</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center space-x-4">
            <div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center font-bold">
              <Heart className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Wishlist</p>
              <p className="text-xl font-black text-gray-900 mt-0.5">{wishlist.length} Items</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center space-x-4">
            <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center font-bold">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Addresses</p>
              <p className="text-xl font-black text-gray-900 mt-0.5">1 Saved</p>
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-xs flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center font-bold">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Security</p>
              <p className="text-xl font-black text-emerald-600 mt-0.5 text-xs bg-emerald-50 px-2 py-0.5 rounded-full inline-block">Protected</p>
            </div>
          </div>
        </div>

        {/* MAIN DASHBOARD CONTENT SECTION */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          
          {/* Left Sidebar Navigation Tabs */}
          <div className="lg:col-span-1 bg-white p-3 rounded-2xl border border-gray-200 shadow-xs space-y-1 h-fit">
            <button 
              onClick={() => setActiveTab('orders')}
              className={`w-full flex items-center justify-between p-3 rounded-xl text-xs font-bold transition ${activeTab === 'orders' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center space-x-3">
                <Package className="w-4 h-4" />
                <span>My Orders</span>
              </div>
              <ChevronRight className="w-4 h-4 opacity-60" />
            </button>

            <button 
              onClick={() => setActiveTab('wishlist')}
              className={`w-full flex items-center justify-between p-3 rounded-xl text-xs font-bold transition ${activeTab === 'wishlist' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center space-x-3">
                <Heart className="w-4 h-4" />
                <span>Wishlist ({wishlist.length})</span>
              </div>
              <ChevronRight className="w-4 h-4 opacity-60" />
            </button>

            <button 
              onClick={() => setActiveTab('addresses')}
              className={`w-full flex items-center justify-between p-3 rounded-xl text-xs font-bold transition ${activeTab === 'addresses' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center space-x-3">
                <MapPin className="w-4 h-4" />
                <span>Saved Addresses</span>
              </div>
              <ChevronRight className="w-4 h-4 opacity-60" />
            </button>

            <button 
              onClick={() => setActiveTab('security')}
              className={`w-full flex items-center justify-between p-3 rounded-xl text-xs font-bold transition ${activeTab === 'security' ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              <div className="flex items-center space-x-3">
                <Shield className="w-4 h-4" />
                <span>Login & Security</span>
              </div>
              <ChevronRight className="w-4 h-4 opacity-60" />
            </button>
          </div>

          {/* Right Dynamic Tab Panel */}
          <div className="lg:col-span-3 space-y-6">
            
            {/* ORDERS TAB */}
            {activeTab === 'orders' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <h2 className="text-base font-black text-gray-900">Recent Orders</h2>
                  <span className="text-xs text-gray-400 font-medium">Showing all orders</span>
                </div>

                {orders.length === 0 ? (
                  <div className="text-center py-16 space-y-3">
                    <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center mx-auto">
                      <Package className="w-8 h-8" />
                    </div>
                    <p className="text-sm font-bold text-gray-700">No orders placed yet!</p>
                    <p className="text-xs text-gray-400 max-w-xs mx-auto">When you place orders, they will appear here with live tracking details.</p>
                    <Link to="/shop" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow transition mt-2">
                      Start Shopping
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-2xl p-5 hover:border-indigo-600 transition space-y-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-100 pb-3 text-xs">
                          <div>
                            <span className="text-gray-400 font-medium">Order ID: </span>
                            <span className="font-bold text-gray-900">#{order.id.slice(0, 8)}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2.5 py-1 rounded-full font-bold flex items-center space-x-1">
                              <CheckCircle2 className="w-3 h-3" />
                              <span>{order.status || 'Delivered'}</span>
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
                              <img src={order.image || "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300"} alt="Product" className="w-full h-full object-cover" />
                            </div>
                            <div>
                              <h3 className="font-bold text-gray-900 text-xs sm:text-sm line-clamp-1">{order.productName || 'Eco-Friendly Product'}</h3>
                              <p className="text-xs text-gray-500 mt-0.5">Qty: {order.quantity || 1}</p>
                              <p className="text-sm font-extrabold text-indigo-600 mt-1">₹{order.totalAmount || order.price || '999'}</p>
                            </div>
                          </div>

                          <button onClick={() => toast.success(`Opening tracking details for #${order.id.slice(0,8)}`)} className="hidden sm:flex items-center space-x-1 bg-gray-900 hover:bg-black text-white px-4 py-2 rounded-xl text-xs font-bold transition">
                            <Truck className="w-3.5 h-3.5" />
                            <span>Track Order</span>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* WISHLIST TAB */}
            {activeTab === 'wishlist' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <h2 className="text-base font-black text-gray-900">My Wishlist</h2>
                  <span className="text-xs text-gray-400 font-medium">{wishlist.length} items saved</span>
                </div>

                {wishlist.length === 0 ? (
                  <div className="text-center py-16 space-y-3">
                    <div className="w-16 h-16 bg-gray-50 text-gray-400 rounded-full flex items-center justify-center mx-auto">
                      <Heart className="w-8 h-8" />
                    </div>
                    <p className="text-sm font-bold text-gray-700">Your wishlist is empty!</p>
                    <p className="text-xs text-gray-400 max-w-xs mx-auto">Explore our store and save your favorite eco-friendly products here.</p>
                    <Link to="/shop" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-5 py-2.5 rounded-xl shadow transition mt-2">
                      Browse Shop
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {wishlist.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-2xl p-4 flex flex-col justify-between space-y-3 bg-gray-50/50 hover:border-indigo-600 transition">
                        <div className="flex space-x-4 items-center">
                          <div className="w-20 h-20 bg-white rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
                            <img src={item.image || item.imageUrl || "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=300"} alt={item.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-gray-900 text-xs line-clamp-1">{item.name || item.productName}</h4>
                            <p className="text-sm font-black text-indigo-600 mt-1">₹{item.price}</p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-gray-200/60">
                          <button 
                            onClick={() => handleMoveToCart(item)}
                            className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-bold py-2 rounded-xl transition shadow-xs flex items-center justify-center space-x-1.5 mr-2"
                          >
                            <ShoppingCart className="w-3.5 h-3.5" />
                            <span>Move to Cart</span>
                          </button>
                          <button 
                            onClick={() => {
                              removeFromWishlist(item.id);
                              toast.success('Removed from wishlist');
                            }}
                            className="p-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition"
                            title="Remove from Wishlist"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ADDRESSES TAB */}
            {activeTab === 'addresses' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
                <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <h2 className="text-base font-black text-gray-900">Saved Addresses</h2>
                  <button onClick={() => toast('Add Address feature coming soon!', { icon: '📍' })} className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 text-xs font-bold px-3 py-1.5 rounded-xl transition">
                    + Add New Address
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border-2 border-indigo-600 bg-indigo-50/30 rounded-2xl p-4 space-y-2 relative">
                    <span className="absolute top-4 right-4 bg-indigo-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">Default</span>
                    <p className="font-bold text-gray-900 text-xs">{user?.name || 'Customer Name'}</p>
                    <p className="text-xs text-gray-600 leading-relaxed">Flat 402, Elegant Heights, Cyber City, DLF Phase 2, Gurugram, Haryana - 122002</p>
                    <p className="text-xs font-bold text-gray-700 mt-1">Phone: {user?.phone || '+91 98765 43210'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* SECURITY TAB */}
            {activeTab === 'security' && (
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-xs space-y-6">
                <h2 className="text-base font-black text-gray-900 border-b border-gray-100 pb-4">Login & Security Settings</h2>
                <div className="space-y-4 text-xs text-gray-700">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <p className="font-bold text-gray-900">Password</p>
                      <p className="text-gray-500 mt-0.5">Last updated recently</p>
                    </div>
                    <button 
                      onClick={() => setIsModalOpen(true)} 
                      className="bg-white border border-gray-300 hover:bg-gray-100 font-bold px-4 py-2 rounded-xl transition shadow-xs"
                    >
                      Change
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                    <div>
                      <p className="font-bold text-gray-900">Two-Step Verification (2SV)</p>
                      <p className="text-gray-500 mt-0.5">Protect your account with OTP security</p>
                    </div>
                    <span className="bg-emerald-100 text-emerald-800 font-bold px-3 py-1 rounded-full text-[10px]">Enabled</span>
                  </div>
                </div>
              </div>
            )}

          </div>

        </div>

      </div>

      {/* --- LOGOUT CONFIRMATION POPUP MODAL --- */}
      {isLogoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center space-y-4 relative border border-gray-100">
            <div className="w-14 h-14 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto shadow-inner">
              <AlertTriangle className="w-7 h-7" />
            </div>
            
            <div className="space-y-1">
              <h3 className="text-lg font-black text-gray-900">Sign Out Confirmation</h3>
              <p className="text-xs text-gray-500 leading-relaxed">
                Are you sure you want to sign out of your account? You will need to login again to access your orders and wishlist.
              </p>
            </div>

            <div className="flex space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setIsLogoutModalOpen(false)}
                disabled={logoutLoading}
                className="w-1/2 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-xs font-bold hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmLogout}
                disabled={logoutLoading}
                className="w-1/2 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition shadow-md shadow-red-600/20 disabled:opacity-50"
              >
                {logoutLoading ? 'Signing Out...' : 'Yes, Sign Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- EDIT PROFILE MODAL --- */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <div className="flex justify-between items-center mb-4 border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900">Edit Profile</h3>
              <button 
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Full Name</label>
                <input 
                  type="text" 
                  value={editName} 
                  onChange={(e) => setEditName(e.target.value)} 
                  placeholder="Enter your name"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Phone Number</label>
                <input 
                  type="text" 
                  value={editPhone} 
                  onChange={(e) => setEditPhone(e.target.value)} 
                  placeholder="Enter phone number"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Email (Cannot be changed)</label>
                <input 
                  type="email" 
                  value={user?.email || ''} 
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 bg-gray-100 rounded-md text-sm text-gray-500 cursor-not-allowed"
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="w-1/2 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editLoading}
                  className="w-1/2 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {editLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- PASSWORD CHANGE MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative">
            <div className="flex justify-between items-center mb-4 border-b pb-3">
              <h3 className="text-lg font-bold text-gray-900">Change Password</h3>
              <button 
                onClick={() => { setIsModalOpen(false); setError(''); setSuccessMessage(''); }}
                className="text-gray-400 hover:text-gray-600 font-bold text-lg"
              >
                &times;
              </button>
            </div>

            {error && <div className="mb-3 p-2 bg-rose-50 text-rose-600 text-xs rounded font-semibold">{error}</div>}
            {successMessage && <div className="mb-3 p-2 bg-emerald-50 text-emerald-600 text-xs rounded font-semibold">{successMessage}</div>}

            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Current Password</label>
                <input 
                  type="password" 
                  value={oldPassword} 
                  onChange={(e) => setOldPassword(e.target.value)} 
                  placeholder="Enter current password"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">New Password</label>
                <input 
                  type="password" 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  placeholder="Enter new password"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Confirm New Password</label>
                <input 
                  type="password" 
                  value={confirmPassword} 
                  onChange={(e) => setConfirmPassword(e.target.value)} 
                  placeholder="Re-enter new password"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <button 
                  type="button" 
                  onClick={handleForgotPasswordMail}
                  className="text-xs text-indigo-600 hover:underline font-semibold"
                >
                  Forgot Password? (Send mail code)
                </button>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="w-1/2 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-semibold hover:bg-gray-100 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={securityLoading}
                  className="w-1/2 py-2 bg-indigo-600 text-white rounded-md text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {securityLoading ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}